import { Calendar, Clock, Globe, Headphones, Loader, Mail, Music, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { useProfileStore } from "../stores/profileStore";
import { useRecentlyPlayedStore } from "../stores/recentlyPlayedStore";
import { useStatsStore } from "../stores/statsStore";

export const Profile = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const stats = useStatsStore();
  const { getAllAlbums } = useLocalLibraryStore();
  const { profile } = useProfileStore();
  const { getRecentTracks } = useRecentlyPlayedStore();

  useEffect(() => {
    const loadProfileData = async () => {
      setIsLoading(true);
      try {
        // Simulate loading profile data
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading profile data:", err);
        setError("Failed to load profile data");
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader className="w-8 h-8 animate-spin text-yellow-500" />
        <p className="text-zinc-400 mt-4">Loading your profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4">Profile</h1>
        <p className="text-zinc-400">{error}</p>
      </div>
    );
  }

  const listenerType = stats.getListenerType();
  const uniqueTracks = stats.getUniqueTracksPlayed();
  const uniqueArtists = stats.getUniqueArtistsPlayed();
  const albums = getAllAlbums();
  const recentTracks = getRecentTracks();

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-6 mb-8">
        {profile.avatar ? (
          <img src={profile.avatar} alt={profile.name} className="w-32 h-32 rounded-full object-cover" />
        ) : (
          <div className="w-32 h-32 bg-zinc-800 rounded-full flex items-center justify-center">
            <User className="w-16 h-16 text-zinc-400" />
          </div>
        )}
        <div>
          <h1 className="text-4xl font-bold mb-2">{profile.name}</h1>
          <div className="flex items-center gap-2 text-zinc-400">
            <Mail className="w-4 h-4" />
            <span>{profile.email || "Email not available"}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-zinc-900 p-6 rounded-lg">
          <h2 className="text-xl font-bold mb-4">Account Details</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-zinc-400" />
              <div>
                <p className="text-sm text-zinc-400">Listener Type</p>
                <p className="font-medium">{listenerType.type}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-zinc-400" />
              <div>
                <p className="text-sm text-zinc-400">Member Since</p>
                <p className="font-medium">
                  {new Date(profile.memberSince).toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 p-6 rounded-lg">
          <h2 className="text-xl font-bold mb-4">Stats</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-3xl font-bold">{uniqueTracks}</p>
              <p className="text-sm text-zinc-400">Unique Tracks</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{uniqueArtists}</p>
              <p className="text-sm text-zinc-400">Unique Artists</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{albums.length}</p>
              <p className="text-sm text-zinc-400">Albums</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.getLikedSongsCount()}</p>
              <p className="text-sm text-zinc-400">Liked Songs</p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 p-6 rounded-lg">
          <h2 className="text-xl font-bold mb-4">Listening Activity</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-zinc-400" />
              <div>
                <p className="text-sm text-zinc-400">Total Listening Time</p>
                <p className="font-medium">{Math.round(stats.totalPlayTime / 60)} minutes</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Headphones className="w-5 h-5 text-zinc-400" />
              <div>
                <p className="text-sm text-zinc-400">Current Streak</p>
                <p className="font-medium">{stats.getCurrentStreak()} days</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Music className="w-5 h-5 text-zinc-400" />
              <div>
                <p className="text-sm text-zinc-400">Discovery Rate</p>
                <p className="font-medium">{Math.round(stats.getListenerMetrics().uniqueArtistsRatio * 100)}%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 p-6 rounded-lg">
          <h2 className="text-xl font-bold mb-4">Recent Activity</h2>
          <div className="space-y-2">
            {recentTracks.slice(0, 5).map((track, index) => (
              <div key={index} className="flex items-center gap-2">
                <Music className="w-4 h-4 text-zinc-400" />
                <p className="text-sm truncate">
                  {track.title} - {track.artist}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
